import { makeAutoObservable, reaction, type IReactionDisposer, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { logger } from 'shared/lib'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import {
  type ICrmUserIntegrationMeta,
  type IParamsPagination,
  type Integration,
  IntegrationKey,
  IntegrationsApi,
} from 'entities/Integrations'
import { IntegrationCrmUser } from 'entities/Integrations/model/IntegrationCrmUser'
import type { IEntitiesStore } from 'entities/EntitiesExport'

const defaultPagination: IParamsPagination = {
  page: 1,
  limit: 10,
}

export type IGoogleCalendarAccount = IntegrationCrmUser<ICrmUserIntegrationMeta>

export class IntegrationGoogleCalendarSettingsStore {
  private _opened = true
  private _accounts = new Map<number, IGoogleCalendarAccount>()
  private _disconnectingAccountId: number | null = null
  private _paginationData = defaultPagination
  private _total = 0
  private _mappedTotal = 0
  private _disposeChangePagination: IReactionDisposer | null = null

  constructor(private _integration: Integration, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = () => {
    this._entities.usersStore.initUsersOrganization()
    this.fetchAccounts()
    this._reactionChange()
  }

  private _reactionChange = () => {
    this._disposeChangePagination?.()
    this._disposeChangePagination = reaction(
      () => this._paginationData,
      () => this.fetchAccounts()
    )
  }

  dispose = () => {
    this._paginationData = defaultPagination
    this._total = 0
    this._disposeChangePagination?.()
  }

  fetchAccounts = async () => {
    try {
      const { data } = await IntegrationsApi.getIntegrationCalendarUsersList(
        IntegrationKey.google_calendar,
        this._paginationData
      )

      runInAction(() => {
        this._total = data.meta.total ?? 0
        this._mappedTotal = data.meta?.mapped ?? 0
        this._accounts.clear()
        data.data.forEach((account) => {
          this._accounts.set(account.id, new IntegrationCrmUser<ICrmUserIntegrationMeta>(account))
        })
      })
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Failed to load Google Calendar accounts',
      })
    }
  }

  onChangePagination = (page: number, limit: number) => {
    this._paginationData = { page, limit }
  }

  get opened() {
    return this._opened
  }

  get organizationMembers() {
    return this._entities.usersStore.users.filter((user) => Boolean(user.email))
  }

  get accounts() {
    return Array.from(this._accounts.values())
  }

  get mappedAccountsCount() {
    return this._mappedTotal
  }

  get accountsCount() {
    return this._total
  }

  get total() {
    return this._total
  }

  get pagination() {
    return this._paginationData
  }

  get disconnectingAccountId() {
    return this._disconnectingAccountId
  }

  onToggle = () => {
    this._opened = !this._opened
  }

  onDisconnect = (accountId: number) => {
    const externalUserId = this._accounts.get(accountId)?.origin.external_user_id
    if (!externalUserId) return

    const modalId = nanoid()

    modalStore.addModal({
      id: modalId,
      type: ModalTypeList.WARNING,
      title: 'Disconnect Google account?',
      desc: "AI Agents using this account's calendars will stop booking appointments",
      primaryAction: {
        text: 'Disconnect',
        onAction: async () => {
          await this._disconnectAccount(accountId, externalUserId)
          modalStore.closeModal(modalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.closeModal(modalId),
      },
    })
  }

  private _disconnectAccount = async (accountId: number, externalUserId: string) => {
    runInAction(() => {
      this._disconnectingAccountId = accountId
    })

    try {
      await IntegrationsApi.disconnectGoogleCalendarAccount(externalUserId)

      const isLastAccountOnPage = this.accounts.length === 1 && this._paginationData.page > 1

      runInAction(() => {
        if (isLastAccountOnPage) {
          this._paginationData = { ...this._paginationData, page: this._paginationData.page - 1 }
        }
      })

      if (!isLastAccountOnPage) {
        await this.fetchAccounts()
      }
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Failed to disconnect Google account',
      })
    } finally {
      runInAction(() => {
        this._disconnectingAccountId = null
      })
    }
  }

  onUserChange = async (accountId: number, userId: number) => {
    const account = this._accounts.get(accountId)
    if (!account) return

    const previousMappedUserId = account.mapped_user_id

    runInAction(() => {
      account.mapped_user_id = userId || undefined
    })

    try {
      const { data } = await IntegrationsApi.putIntegrationCalendarUserFieldValue(
        this._integration.key,
        accountId,
        { mapped_user_id: userId }
      )

      runInAction(() => {
        this._accounts.set(data.id, new IntegrationCrmUser<ICrmUserIntegrationMeta>(data))

        const isNowMapped = Boolean(data.mapped_user_id)
        if (previousMappedUserId && !isNowMapped) {
          this._mappedTotal -= 1
        }
        if (!previousMappedUserId && isNowMapped) {
          this._mappedTotal += 1
        }
      })
    } catch (e) {
      logger.error(e)

      runInAction(() => {
        account.mapped_user_id = previousMappedUserId
      })

      showToast({
        type: 'error',
        title: 'Failed to update Google account mapping',
      })
    }
  }
}
